// Screen 2 — Google Drive–style folder browser (client-side tree from LIST_FILES).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FileNode, PlanTier, XferProgress } from '../protocol';
import { nodeAtPath, trailAtPath } from '../fileTreeNav';
import { formatSize } from '../format';
import { isHcloud } from '../crypto';
import { fileIcon, isImageFileName } from '../fileIcons';
import { isPaidTier, PAID_FEATURE_HINT } from '../planTier';

const DRAG_PATH_KEY = 'application/x-homecloud-path';

export interface FileExplorerProps {
  root: FileNode;
  hostName?: string;
  onRefresh: () => void;
  /** Load a subdirectory listing from the host (shallow tree). */
  onListFolder?: (relPath: string) => void;
  onRelPathChange?: (relPath: string) => void;
  onOpenFile: (node: FileNode) => void;
  onDownloadFile: (node: FileNode) => void;
  onShareFile: (node: FileNode) => void;
  onDeleteEntry: (relPath: string, kind: 'dir' | 'file') => void;
  onMoveEntry: (fromPath: string, toDirPath: string) => void;
  onUploadFiles: (files: File[], destDir: string) => void;
  onCreateFolder: (parentPath: string, name: string) => void;
  planTier?: PlanTier | null;
  thumbUrls?: Record<string, string>;
  onRequestThumb?: (relPath: string) => void;
  encrypt: boolean;
  onToggleEncrypt: () => void;
  transfer: XferProgress | null;
  onCancelTransfer?: () => void;
}

function IconGlyph({ kind }: { kind: string }): JSX.Element {
  const cls = `drive-type-icon drive-type-${kind}`;
  return <span className={cls} aria-hidden />;
}

function ToolbarIcon({ name }: { name: 'upload' | 'parent' | 'newfolder' }): JSX.Element {
  if (name === 'upload') {
    return (
      <svg className="drive-tool-icon" viewBox="0 0 24 24" aria-hidden>
        <path
          fill="currentColor"
          d="M12 6.5a1 1 0 01.7.3l3.5 3.5a1 1 0 11-1.4 1.4L13 10.1V16a1 1 0 11-2 0v-5.9l-1.8 1.6a1 1 0 11-1.4-1.4l3.5-3.5a1 1 0 01.7-.3z"
        />
        <path
          fill="currentColor"
          d="M5 17.25c0-.69.56-1.25 1.25-1.25h11.5c.69 0 1.25.56 1.25 1.25V19.5c0 .69-.56 1.25-1.25 1.25H6.25A1.25 1.25 0 015 19.5v-2.25z"
        />
      </svg>
    );
  }
  if (name === 'parent') {
    return (
      <svg className="drive-tool-icon" viewBox="0 0 24 24" aria-hidden>
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7 14V8.5a1.5 1.5 0 011.5-1.5H14"
        />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10 6.5L7 9.5l3 3"
        />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          d="M5 18h14"
        />
      </svg>
    );
  }
  return (
    <svg className="drive-tool-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M4 7.5h7.2L12.5 9.5H19a1.5 1.5 0 011.5 1.5v7A1.5 1.5 0 0119 19.5H5A1.5 1.5 0 013.5 18v-8A1.5 1.5 0 015 7.5h-.5z"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        d="M12 11.5v5M9.5 14h5"
      />
    </svg>
  );
}

export function FileExplorer({
  root,
  hostName,
  onRefresh,
  onListFolder,
  onRelPathChange,
  onOpenFile,
  onDownloadFile,
  onShareFile,
  onDeleteEntry,
  onMoveEntry,
  onUploadFiles,
  onCreateFolder,
  planTier,
  thumbUrls = {},
  onRequestThumb,
  encrypt,
  onToggleEncrypt,
  transfer,
  onCancelTransfer,
}: FileExplorerProps): JSX.Element {
  /** Current folder relative to the shared root ("" = root). Survives LIST_FILES refreshes. */
  const [relPath, setRelPath] = useState('');
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);
  const crumbRef = useRef<HTMLElement | null>(null);
  const [crumbOverflowEnd, setCrumbOverflowEnd] = useState(false);

  const updateCrumbOverflow = useCallback(() => {
    const el = crumbRef.current;
    if (!el) return;
    const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setCrumbOverflowEnd(canScrollRight);
  }, []);

  const scrollCrumbToEnd = () => {
    const el = crumbRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
  };

  const current = useMemo(() => nodeAtPath(root, relPath), [root, relPath]);
  const path = useMemo(() => trailAtPath(root, relPath), [root, relPath]);

  useEffect(() => {
    const el = crumbRef.current;
    if (!el) return;
    if (path.length > 1) {
      el.scrollLeft = el.scrollWidth;
    } else {
      el.scrollLeft = 0;
    }
    updateCrumbOverflow();
    const ro = new ResizeObserver(() => updateCrumbOverflow());
    ro.observe(el);
    el.addEventListener('scroll', updateCrumbOverflow, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', updateCrumbOverflow);
    };
  }, [path, hostName, root.name, updateCrumbOverflow]);

  const folderLoading = current.kind === 'dir' && current.children === undefined;

  const entries = useMemo(() => {
    const kids = current.children ?? [];
    return [...kids].sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'dir' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [current]);

  useEffect(() => {
    if (!onRequestThumb) return;
    for (const node of entries) {
      if (
        node.kind === 'file' &&
        isImageFileName(node.name) &&
        !thumbUrls[node.path]
      ) {
        onRequestThumb(node.path);
      }
    }
  }, [entries, onRequestThumb, thumbUrls]);

  const goToRel = (next: string) => {
    setRelPath(next);
    onRelPathChange?.(next);
    const n = nodeAtPath(root, next);
    if (n.kind === 'dir' && n.children === undefined) onListFolder?.(next);
  };

  const enter = (node: FileNode) => {
    if (node.kind !== 'dir') return;
    goToRel(node.path);
  };

  const crumbTo = (index: number) => {
    const node = path[index];
    goToRel(node?.path ?? '');
  };

  const goUp = () => {
    if (!relPath) return;
    const parts = relPath.split('/');
    parts.pop();
    goToRel(parts.join('/'));
  };

  const pct =
    transfer && transfer.total > 0 ? Math.round((transfer.done / transfer.total) * 100) : 0;

  const onFolderDragOver = (e: React.DragEvent, folderPath: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(folderPath);
  };

  const onFolderDrop = (e: React.DragEvent, folderPath: string) => {
    e.preventDefault();
    setDropTarget(null);
    const from = e.dataTransfer.getData(DRAG_PATH_KEY) || e.dataTransfer.getData('text/plain');
    if (from) onMoveEntry(from, folderPath);
  };

  return (
    <section className="drive-explorer">
      <div className="drive-toolbar">
        <div className="drive-toolbar-path-row">
          <div
            className={`drive-breadcrumb-wrap${crumbOverflowEnd ? ' drive-breadcrumb-wrap--overflow' : ''}`}
          >
            <nav ref={crumbRef} className="drive-breadcrumb" aria-label="Folder path">
              {path.map((node, i) => (
                <span key={`${node.path}-${i}`} className="drive-crumb-wrap">
                  {i > 0 && <span className="drive-crumb-sep">›</span>}
                  <button
                    type="button"
                    className={i === path.length - 1 ? 'drive-crumb current' : 'drive-crumb'}
                    onClick={() => crumbTo(i)}
                  >
                    {i === 0 ? hostName || root.name || 'My Drive' : node.name}
                  </button>
                </span>
              ))}
            </nav>
            {crumbOverflowEnd && (
              <button
                type="button"
                className="drive-crumb-scroll-more"
                onClick={scrollCrumbToEnd}
                title="Show end of path"
                aria-label="Show end of path"
              >
                ›
              </button>
            )}
          </div>
          <button
            type="button"
            className="drive-tool-btn drive-tool-btn-refresh"
            onClick={onRefresh}
            title="Refresh"
            aria-label="Refresh"
          >
            ↻
          </button>
        </div>
        <div className="drive-toolbar-actions">
          <button
            type="button"
            className={`drive-tool-btn${encrypt ? ' active' : ''}`}
            onClick={onToggleEncrypt}
            title="Encrypt uploads as .hcloud"
            aria-pressed={encrypt}
          >
            🔒
          </button>
          <button
            type="button"
            className="drive-tool-btn"
            disabled={transfer !== null}
            onClick={() => fileInput.current?.click()}
            title="Upload files"
            aria-label="Upload files"
          >
            <ToolbarIcon name="upload" />
          </button>
          <button
            type="button"
            className="drive-tool-btn"
            disabled={transfer !== null || !isPaidTier(planTier)}
            onClick={() => {
              if (!isPaidTier(planTier)) {
                window.alert(PAID_FEATURE_HINT);
                return;
              }
              folderInput.current?.click();
            }}
            title="Upload folder tree into this directory (paid)"
            aria-label="Upload folder"
          >
            📁↑
          </button>
          <button
            type="button"
            className="drive-tool-btn"
            disabled={!relPath || transfer !== null}
            onClick={goUp}
            title="Go up one folder level"
            aria-label="Go up one folder level"
          >
            <ToolbarIcon name="parent" />
          </button>
          <button
            type="button"
            className="drive-tool-btn"
            disabled={transfer !== null}
            onClick={() => {
              const name = window.prompt('New folder name');
              if (name) onCreateFolder(current.path, name);
            }}
            title="New folder"
            aria-label="New folder"
          >
            <ToolbarIcon name="newfolder" />
          </button>
          <input
            ref={fileInput}
            type="file"
            hidden
            multiple
            onChange={(e) => {
              const picked = e.target.files;
              if (!picked || picked.length === 0) return;
              const batch = Array.from(picked);
              e.target.value = '';
              onUploadFiles(batch, current.path);
            }}
          />
          <input
            ref={folderInput}
            type="file"
            hidden
            multiple
            // @ts-expect-error webkitdirectory is non-standard but widely supported
            webkitdirectory=""
            onChange={(e) => {
              const picked = e.target.files;
              if (!picked || picked.length === 0) return;
              const batch = Array.from(picked);
              e.target.value = '';
              onUploadFiles(batch, current.path);
            }}
          />
        </div>
      </div>

      {transfer && (
        <div className="drive-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <span>
            {transfer.dir === 'down' ? 'Downloading' : 'Uploading'} {transfer.name} — {pct}%
          </span>
          {onCancelTransfer && (
            <button type="button" className="btn ghost drive-progress-cancel" onClick={onCancelTransfer}>
              Cancel
            </button>
          )}
          <div className="drive-progress-track">
            <div className="drive-progress-bar" style={{ width: `${transfer.total ? pct : 5}%` }} />
          </div>
        </div>
      )}

      <div className="drive-list-head" aria-hidden>
        <span className="drive-col-name">Name</span>
        <span className="drive-col-size">Size</span>
      </div>

      {entries.length === 0 ? (
        <div
          className={`drive-empty${dropTarget === relPath ? ' drive-drop-target' : ''}`}
          onDragOver={(e) => onFolderDragOver(e, relPath)}
          onDragLeave={() => setDropTarget(null)}
          onDrop={(e) => onFolderDrop(e, relPath)}
        >
          <p>{folderLoading ? 'Loading folder…' : 'This folder is empty.'}</p>
        </div>
      ) : (
        <ul className="drive-list">
          {entries.map((node) => {
            const icon = fileIcon(node.name, node.kind);
            const isDrop = node.kind === 'dir' && dropTarget === node.path;
            return (
              <li key={node.path}>
                {node.kind === 'dir' ? (
                  <div
                    className={`drive-row file${isDrop ? ' drive-drop-target' : ''}`}
                    onDragOver={(e) => onFolderDragOver(e, node.path)}
                    onDragLeave={() => setDropTarget(null)}
                    onDrop={(e) => onFolderDrop(e, node.path)}
                  >
                    <button type="button" className="drive-row-main" onClick={() => enter(node)}>
                      <IconGlyph kind={icon} />
                      <span className="drive-col-name">{node.name}</span>
                      <span className="drive-col-size">—</span>
                    </button>
                    <button
                      type="button"
                      className="drive-row-dl"
                      onClick={() => onDeleteEntry(node.path, 'dir')}
                      disabled={transfer !== null}
                      title="Delete folder"
                      aria-label={`Delete ${node.name}`}
                    >
                      🗑
                    </button>
                  </div>
                ) : (
                  <div
                    className="drive-row file"
                    draggable={transfer === null}
                    onDragStart={(e) => {
                      e.dataTransfer.setData(DRAG_PATH_KEY, node.path);
                      e.dataTransfer.setData('text/plain', node.path);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                  >
                    <button type="button" className="drive-row-main" onClick={() => onOpenFile(node)}>
                      {thumbUrls[node.path] ? (
                        <img
                          className="drive-thumb"
                          src={thumbUrls[node.path]}
                          alt=""
                          width={32}
                          height={32}
                        />
                      ) : (
                        <IconGlyph kind={icon} />
                      )}
                      <span className="drive-col-name">
                        {node.name}
                        {isHcloud(node.name) && (
                          <span className="drive-lock" title="Encrypted">🔒</span>
                        )}
                      </span>
                      <span className="drive-col-size">{formatSize(node.size)}</span>
                    </button>
                    <button
                      type="button"
                      className="drive-row-dl"
                      onClick={() => onShareFile(node)}
                      disabled={transfer !== null}
                      title="Share"
                      aria-label={`Share ${node.name}`}
                    >
                      ↗
                    </button>
                    <button
                      type="button"
                      className="drive-row-dl"
                      onClick={() => onDownloadFile(node)}
                      disabled={transfer !== null}
                      title="Download"
                      aria-label={`Download ${node.name}`}
                    >
                      ⬇
                    </button>
                    <button
                      type="button"
                      className="drive-row-dl"
                      onClick={() => onDeleteEntry(node.path, 'file')}
                      disabled={transfer !== null}
                      title="Delete"
                      aria-label={`Delete ${node.name}`}
                    >
                      🗑
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
